import { useState, useEffect, useRef, Fragment } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Check,
  Loader2,
  Wand2,
  Download,
  RotateCcw,
  Save,
  Link,
  FileUp,
  X,
  AlertCircle,
  Sparkles,
} from "lucide-react";
import { invokeFunction } from "@/integrations/supabase/client";
import type { GeneratedTaxonomy, Taxonomy } from "@/lib/learnerHub/types";

interface WorkspaceMaterial {
  id: string;
  fileName: string;
  text: string;
}

interface Props {
  onSave: (taxonomy: GeneratedTaxonomy) => void;
  onGenerateCourse?: (topic: string, files: File[]) => void;
  workspaceMaterials?: WorkspaceMaterial[];
  /** When set, pre-loads this taxonomy into the review screen (used for demo mode). */
  demoPreload?: Taxonomy;
}

interface GenForm {
  domain: string;
  role: string;
  context: string;
  framework: string;
}

interface Material {
  id: string;
  label: string;
  status: "extracting" | "done" | "error";
  text: string | null;
  errorMsg?: string;
}

interface RawUnit {
  id: string;
  label: string;
  type: "application" | "knowledge";
  ind: string;
}

interface RawQual {
  id: string;
  label: string;
  units: RawUnit[];
}

interface RawProcess {
  id: string;
  label: string;
  qualifications: RawQual[];
}

interface RawResult {
  domain: string;
  targetRole: string;
  anchorFramework: string;
  processes: RawProcess[];
}

const EXAMPLE: GenForm = {
  domain: "Minería a rajo abierto y subterránea — Chile",
  role: "Operadores de equipos mina rajo y subterránea (Nivel 2)",
  context: "Certificación de competencias laborales alineada al Marco de Cualificaciones CCM-Eleva para faenas en el norte de Chile",
  framework: "Marco de Cualificaciones para la Minería (CCM-Eleva), DS-132, SENCE",
};

const LOADING_MESSAGES = [
  "Analysing domain structure…",
  "Identifying process areas…",
  "Generating competency blocks…",
  "Writing skill units…",
];

const DOT_COLORS = ["bg-blue-500", "bg-green-500", "bg-amber-500", "bg-red-500", "bg-gray-400"];

const STEP_LABELS = ["Scope", "Generate", "Review"] as const;

const ACCEPTED_TYPES = ".pdf,.txt,.md";

// ─── helpers ──────────────────────────────────────────────────────────────────

function uid() {
  return Math.random().toString(36).slice(2);
}

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function fileToText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsText(file);
  });
}

// ─── component ────────────────────────────────────────────────────────────────

function buildCourseTopic(result: RawResult): string {
  const lines: string[] = [
    `Create a training course for ${result.targetRole} in ${result.domain}.`,
    ``,
    `The course should develop competency across the following taxonomy:`,
    `Framework: ${result.anchorFramework}`,
    ``,
  ];
  for (const process of result.processes) {
    lines.push(`Process area: ${process.label}`);
    for (const qual of process.qualifications) {
      lines.push(`  ${qual.label}`);
      for (const unit of qual.units) {
        lines.push(`    • ${unit.id}: ${unit.label} [${unit.type}]`);
        if (unit.ind) lines.push(`      Assessment: ${unit.ind}`);
      }
    }
    lines.push("");
  }
  lines.push(
    `Design the course to build the skills and knowledge described above. ` +
    `Reference materials are attached where available — use them as primary source content.`
  );
  return lines.join("\n");
}

export default function TaxonomyGeneratorTab({ onSave, onGenerateCourse, workspaceMaterials = [], demoPreload }: Props) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [form, setForm] = useState<GenForm>({ domain: "", role: "", context: "", framework: "" });
  const [materials, setMaterials] = useState<Material[]>([]);
  const [selectedWorkspaceIds, setSelectedWorkspaceIds] = useState<Set<string>>(new Set());
  const [urlInput, setUrlInput] = useState("");
  const [result, setResult] = useState<RawResult | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msgIdx, setMsgIdx] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function stopInterval() {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }
  useEffect(() => () => stopInterval(), []);

  // Demo mode: pre-fill the form so the user can click Generate and see the process run
  useEffect(() => {
    if (!demoPreload) return;
    setForm(EXAMPLE);
    setStep(1);
    setResult(null);
    setSaved(false);
    setError(null);
  }, [demoPreload]);

  // ─── material extraction ───────────────────────────────────────────────────

  function updateMaterial(id: string, patch: Partial<Material>) {
    setMaterials((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  }

  async function extractUrl(rawUrl: string) {
    const url = rawUrl.trim();
    if (!url) return;
    const id = uid();
    setMaterials((prev) => [
      ...prev,
      { id, label: url, status: "extracting", text: null },
    ]);
    setUrlInput("");
    try {
      const { data, error: fnErr } = await invokeFunction("extract-text", { body: { url } });
      if (fnErr || !data?.text) throw new Error((fnErr as { message?: string })?.message || "Could not extract text");
      updateMaterial(id, { status: "done", text: data.text as string });
    } catch (e) {
      updateMaterial(id, {
        status: "error",
        errorMsg: e instanceof Error ? e.message : "Extraction failed",
      });
    }
  }

  async function extractFile(file: File) {
    const id = uid();
    const label = file.name;
    setMaterials((prev) => [...prev, { id, label, status: "extracting", text: null }]);

    try {
      // Plain text: read in browser
      if (file.type === "text/plain" || file.name.endsWith(".txt") || file.name.endsWith(".md")) {
        const text = await fileToText(file);
        updateMaterial(id, { status: "done", text: text.slice(0, 40_000) });
        return;
      }

      // PDF: send to edge function
      const fileBase64 = await fileToBase64(file);
      const { data, error: fnErr } = await invokeFunction("extract-text", {
        body: { fileBase64, fileName: file.name, mimeType: file.type },
      });
      if (fnErr || !data?.text) throw new Error((fnErr as { message?: string })?.message || "Could not extract text");
      updateMaterial(id, { status: "done", text: data.text as string });
    } catch (e) {
      updateMaterial(id, {
        status: "error",
        errorMsg: e instanceof Error ? e.message : "Extraction failed",
      });
    }
  }

  function removeMaterial(id: string) {
    setMaterials((prev) => prev.filter((m) => m.id !== id));
  }

  // ─── generation ───────────────────────────────────────────────────────────

  function buildDemoResult(taxonomy: Taxonomy): RawResult {
    return {
      domain: taxonomy.name,
      targetRole: "Operadores y técnicos de equipos mineros (Nivel 2)",
      anchorFramework: taxonomy.source || "CCM-Eleva",
      processes: taxonomy.processes.map((p) => ({
        id: p.id,
        label: p.label,
        qualifications: p.qualifications.map((q) => ({
          id: q.id,
          label: q.label,
          units: q.units.map((u) => ({
            id: u.id,
            label: u.label,
            type: (u.type ?? "application") as "application" | "knowledge",
            ind: u.ind ?? "",
          })),
        })),
      })),
    };
  }

  async function runGenerate() {
    setError(null);
    setStep(2);
    setMsgIdx(0);
    intervalRef.current = setInterval(() => setMsgIdx((i) => (i + 1) % LOADING_MESSAGES.length), 1800);

    // Demo mode: skip AI call, show loading for a moment then reveal the pre-determined result
    if (demoPreload) {
      await new Promise((resolve) => setTimeout(resolve, 3200));
      stopInterval();
      setResult(buildDemoResult(demoPreload));
      setSaved(true);
      setStep(3);
      return;
    }

    try {
      const referenceMaterials = [
        ...materials.filter((m) => m.status === "done" && m.text).map((m) => m.text as string),
        ...workspaceMaterials.filter((m) => selectedWorkspaceIds.has(m.id)).map((m) => m.text),
      ];

      const { data, error: fnErr } = await invokeFunction("generate-taxonomy", {
        body: {
          domain: form.domain,
          role: form.role,
          context: form.context,
          framework: form.framework,
          referenceMaterials,
        },
      });

      stopInterval();
      if (fnErr) throw new Error((fnErr as { message?: string }).message || "Generation failed");
      if (!data?.taxonomy) throw new Error("No taxonomy in response");

      setResult(data.taxonomy as RawResult);
      setSaved(false);
      setStep(3);
    } catch (err: unknown) {
      stopInterval();
      setError(`Generation failed: ${err instanceof Error ? err.message : String(err)}`);
      setStep(1);
    }
  }

  // ─── save / export / reset ────────────────────────────────────────────────

  function handleSave() {
    if (!result) return;
    const id = `gen-${Date.now()}`;
    const generatedAt = new Date().toISOString().split("T")[0];
    const taxonomy: GeneratedTaxonomy = {
      id,
      customerId: id,
      name: result.domain,
      version: generatedAt,
      source: "AI-generated",
      generated: true,
      generatedAt,
      domain: result.domain,
      targetRole: result.targetRole,
      anchorFramework: result.anchorFramework,
      processes: result.processes.map((p) => ({
        id: p.id,
        label: p.label,
        qualifications: p.qualifications.map((q) => ({
          id: q.id,
          label: q.label,
          level: null,
          units: q.units.map((u) => ({ id: u.id, label: u.label, type: u.type, ind: u.ind })),
        })),
      })),
    };
    onSave(taxonomy);
    setSaved(true);
  }

  function handleExport() {
    if (!result) return;
    const filename = result.domain.toLowerCase().replace(/\s+/g, "-") + ".json";
    const blob = new Blob([JSON.stringify(result, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  function handleReset() {
    setStep(1);
    setResult(null);
    setSaved(false);
    setError(null);
  }

  function handleGenerateCourse() {
    if (!result || !onGenerateCourse) return;
    const topic = buildCourseTopic(result);
    // Convert any successfully extracted reference materials to text files
    const files = materials
      .filter((m) => m.status === "done" && m.text)
      .map((m) => {
        const name = m.label.startsWith("http")
          ? new URL(m.label).hostname + ".txt"
          : m.label.replace(/\.[^.]+$/, "") + ".txt";
        return new File([m.text!], name, { type: "text/plain" });
      });
    onGenerateCourse(topic, files);
  }

  const totalQuals = result ? result.processes.reduce((s, p) => s + p.qualifications.length, 0) : 0;
  const totalUnits = result
    ? result.processes.reduce((s, p) => s + p.qualifications.reduce((qs, q) => qs + q.units.length, 0), 0)
    : 0;
  const extractingCount = materials.filter((m) => m.status === "extracting").length;
  const doneCount = materials.filter((m) => m.status === "done").length + selectedWorkspaceIds.size;

  return (
    <div className="space-y-6">
      {/* Step indicator */}
      <div className="flex items-center">
        {STEP_LABELS.map((label, i) => (
          <Fragment key={label}>
            {i > 0 && <div className="flex-1 h-px bg-border mx-3" />}
            <div
              className={`flex items-center gap-2 ${
                step === i + 1 ? "text-foreground" : step > i + 1 ? "text-primary" : "text-muted-foreground"
              }`}
            >
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${
                  step > i + 1 || step === i + 1
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted border border-border"
                }`}
              >
                {step > i + 1 ? <Check className="h-3.5 w-3.5" /> : i + 1}
              </div>
              <span className="text-sm font-medium hidden sm:inline">{label}</span>
            </div>
          </Fragment>
        ))}
      </div>

      {/* ── Step 1: Scope ── */}
      {step === 1 && (
        <div className="space-y-5 max-w-xl">
          {/* Scope fields */}
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">
                Professional domain <span className="text-destructive">*</span>
              </label>
              <input
                value={form.domain}
                onChange={(e) => setForm({ ...form, domain: e.target.value })}
                placeholder="e.g. minería a rajo abierto, perforación petrolífera"
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium">
                Target population <span className="text-destructive">*</span>
              </label>
              <input
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                placeholder="e.g. operadores de equipos rajo Nivel 2, técnicos de mantenimiento"
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium">Context and purpose</label>
              <textarea
                value={form.context}
                onChange={(e) => setForm({ ...form, context: e.target.value })}
                placeholder="e.g. certificación CCM-Eleva para faenas en el norte de Chile"
                rows={3}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm resize-none focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium">Anchor frameworks</label>
              <input
                value={form.framework}
                onChange={(e) => setForm({ ...form, framework: e.target.value })}
                placeholder="e.g. CCM-Eleva, DS-132, SENCE, ISO 45001"
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
            </div>
          </div>

          {/* Reference materials */}
          <div className="space-y-3 pt-1">
            <div>
              <p className="text-sm font-medium">Reference materials</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Add URLs, upload files, or select from the workspace library — extracted content guides taxonomy generation.
              </p>
            </div>

            {/* Workspace library materials */}
            {workspaceMaterials.length > 0 && (
              <div className="rounded-md border bg-muted/20 divide-y">
                <p className="px-3 py-2 text-xs font-medium text-muted-foreground">From workspace library</p>
                {workspaceMaterials.map((m) => {
                  const checked = selectedWorkspaceIds.has(m.id);
                  return (
                    <label
                      key={m.id}
                      className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-muted/30"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          setSelectedWorkspaceIds((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(m.id);
                            else next.delete(m.id);
                            return next;
                          });
                        }}
                        className="h-3.5 w-3.5 accent-primary shrink-0"
                      />
                      <span className="text-xs truncate flex-1">{m.fileName}</span>
                      <span className="text-xs text-muted-foreground shrink-0">
                        {(m.text.length / 1000).toFixed(0)}k chars
                      </span>
                    </label>
                  );
                })}
              </div>
            )}

            {/* URL input */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Link className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <input
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); extractUrl(urlInput); } }}
                  placeholder="https://example.com/framework"
                  className="flex h-9 w-full rounded-md border border-input bg-background pl-8 pr-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                className="shrink-0"
                disabled={!urlInput.trim()}
                onClick={() => extractUrl(urlInput)}
              >
                Add URL
              </Button>
            </div>

            {/* File picker */}
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_TYPES}
                multiple
                className="hidden"
                onChange={(e) => {
                  Array.from(e.target.files ?? []).forEach(extractFile);
                  e.target.value = "";
                }}
              />
              <Button
                variant="outline"
                size="sm"
                className="gap-2 w-full"
                onClick={() => fileInputRef.current?.click()}
              >
                <FileUp className="h-3.5 w-3.5" />
                Upload PDF or text file
              </Button>
            </div>

            {/* Material chips */}
            {materials.length > 0 && (
              <div className="space-y-1.5">
                {materials.map((m) => (
                  <div
                    key={m.id}
                    className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 text-sm"
                  >
                    {m.status === "extracting" && (
                      <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" />
                    )}
                    {m.status === "done" && (
                      <Check className="h-3.5 w-3.5 shrink-0 text-green-600" />
                    )}
                    {m.status === "error" && (
                      <AlertCircle className="h-3.5 w-3.5 shrink-0 text-destructive" />
                    )}
                    <span className="flex-1 truncate text-xs">
                      {m.label}
                    </span>
                    {m.status === "done" && (
                      <span className="text-xs text-muted-foreground shrink-0">
                        {((m.text?.length ?? 0) / 1000).toFixed(0)}k chars
                      </span>
                    )}
                    {m.status === "error" && (
                      <span className="text-xs text-destructive shrink-0 truncate max-w-[120px]" title={m.errorMsg}>
                        {m.errorMsg}
                      </span>
                    )}
                    <button
                      onClick={() => removeMaterial(m.id)}
                      className="shrink-0 rounded p-0.5 hover:bg-muted text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                {doneCount > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {doneCount} material{doneCount !== 1 ? "s" : ""} ready
                    {extractingCount > 0 ? ` · ${extractingCount} still extracting` : ""}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Actions row */}
          <div className="flex items-center justify-between pt-1">
            <Button variant="ghost" size="sm" onClick={() => setForm(EXAMPLE)}>
              Try mining example
            </Button>
            <Button
              disabled={!form.domain.trim() || !form.role.trim() || extractingCount > 0}
              onClick={runGenerate}
              className="gap-2"
            >
              <Wand2 className="h-4 w-4" />
              Generate competency map
            </Button>
          </div>

          {extractingCount > 0 && (
            <p className="text-xs text-muted-foreground">
              Waiting for {extractingCount} material{extractingCount !== 1 ? "s" : ""} to finish extracting…
            </p>
          )}
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
      )}

      {/* ── Step 2: Loading ── */}
      {step === 2 && (
        <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
          <Loader2 className="h-10 w-10 animate-spin text-primary" />
          <h3 className="text-lg font-semibold">{form.domain}</h3>
          <p className="text-sm text-muted-foreground">{LOADING_MESSAGES[msgIdx]}</p>
          {doneCount > 0 && (
            <p className="text-xs text-muted-foreground">
              Using {doneCount} reference material{doneCount !== 1 ? "s" : ""}
            </p>
          )}
        </div>
      )}

      {/* ── Step 3: Review ── */}
      {step === 3 && result && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Process areas</p>
                <p className="text-2xl font-semibold mt-0.5">{result.processes.length}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Competency blocks</p>
                <p className="text-2xl font-semibold mt-0.5">{totalQuals}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Skill units</p>
                <p className="text-2xl font-semibold mt-0.5">{totalUnits}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Framework</p>
                <p className="text-xs font-medium leading-tight mt-1 line-clamp-3">
                  {result.anchorFramework}
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-4">
            {result.processes.map((process, pi) => {
              const unitCount = process.qualifications.reduce((s, q) => s + q.units.length, 0);
              return (
                <Card key={process.id}>
                  <CardHeader className="py-3 px-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${DOT_COLORS[pi % DOT_COLORS.length]}`} />
                        <CardTitle className="text-sm">{process.label}</CardTitle>
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
                        {process.qualifications.length} blocks · {unitCount} skills
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    {process.qualifications.map((qual, qi) => (
                      <div key={qual.id} className={qi > 0 ? "border-t" : ""}>
                        <div className="px-4 py-2 bg-muted/40 text-xs font-medium">
                          {qual.label} ({qual.units.length})
                        </div>
                        {qual.units.map((unit, ui) => (
                          <div
                            key={unit.id}
                            className={`flex items-start gap-3 px-4 py-2.5 ${ui > 0 ? "border-t border-border/40" : ""}`}
                          >
                            <span className="font-mono text-xs text-muted-foreground shrink-0 w-14 pt-0.5">
                              {unit.id}
                            </span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start gap-2 flex-wrap">
                                <span className="text-sm">{unit.label}</span>
                                <Badge
                                  variant="outline"
                                  className={`text-xs shrink-0 ${
                                    unit.type === "application"
                                      ? "bg-blue-50 text-blue-700 border-blue-200"
                                      : "bg-amber-50 text-amber-700 border-amber-200"
                                  }`}
                                >
                                  {unit.type}
                                </Badge>
                              </div>
                              {unit.ind && (
                                <p className="text-xs text-muted-foreground mt-0.5 italic">{unit.ind}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {saved ? (
              <span className="flex items-center gap-1.5 text-sm text-green-600">
                <Check className="h-4 w-4" />
                Saved to taxonomy library
              </span>
            ) : (
              <Button onClick={handleSave} className="gap-2">
                <Save className="h-4 w-4" />
                Save to taxonomy library
              </Button>
            )}
            {onGenerateCourse && (
              <Button variant="outline" onClick={handleGenerateCourse} className="gap-2">
                <Sparkles className="h-4 w-4" />
                Generate course
              </Button>
            )}
            <Button variant="outline" onClick={handleExport} className="gap-2">
              <Download className="h-4 w-4" />
              Export JSON
            </Button>
            <Button variant="ghost" onClick={handleReset} className="gap-2 text-muted-foreground ml-auto">
              <RotateCcw className="h-3.5 w-3.5" />
              Start over
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
